import { Alert } from '@creditornot/cb-alert';
import React, { useContext, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { updateSubmissionData } from 'apis/legitimizer/kyb/api';
import { Beneficiaries, KybSubmissionData, UboDetail } from 'apis/legitimizer/types';

import BasicInfoFormFields from './BasicInfoFormFields';
import { LoadingOverlay } from './LoadingOverlay';
import { SectionForm } from './SectionForm';
import { useTaxInfoVerification } from './useTaxInfoVerification';
import { getBasicInfoSchema, getBusinessRepresentativesSchema } from './validation';
import { KybProcessContext } from '../KybProcessContext';
import { SectionConfigurationContext } from '../SectionConfigurationContext';
import { transformBasicInfoFormValuesToApi, transformKybSubmissionToBasicInfoFormValues } from '../transformers';
import { BasicInfoFormValues, BusinessRepresentative } from '../types';
import {
  useBasicInfoFieldsConfigMemo,
  useBeneficiaryDetailsFieldsConfigMemo,
  useBusinessRepresentativeFieldsConfigMemo,
} from '../useFieldsConfigMemo';

export type Props = {
  feedback?: string;
  submissionData: KybSubmissionData;
  onSubmitSuccess: (section: string, skipSection?: boolean) => Promise<void>;
};

const BasicInfo = ({ feedback, submissionData, onSubmitSuccess }: Props) => {
  const { formatMessage: t } = useIntl();

  const sectionConfiguration = useContext(SectionConfigurationContext);
  const { countryCode, processId, merchantId } = useContext(KybProcessContext);

  const basicInfoFieldsConfig = useBasicInfoFieldsConfigMemo({ sectionConfiguration });
  const businessRepresentativeFieldsConfig = useBusinessRepresentativeFieldsConfigMemo({ sectionConfiguration });
  const uboFieldsConfig = useBeneficiaryDetailsFieldsConfigMemo({ sectionConfiguration });

  const basicInfoFormSchema = useMemo(() => {
    return basicInfoFieldsConfig.businessRepresentatives.required
      ? getBasicInfoSchema(false, basicInfoFieldsConfig).concat(
          getBusinessRepresentativesSchema(false, businessRepresentativeFieldsConfig, uboFieldsConfig),
        )
      : getBasicInfoSchema(false, basicInfoFieldsConfig);
  }, [basicInfoFieldsConfig, businessRepresentativeFieldsConfig, uboFieldsConfig]);

  const initialFormValues = useMemo(() => {
    return transformKybSubmissionToBasicInfoFormValues(submissionData.basicCompanyInfo, countryCode);
  }, [submissionData, countryCode]);

  const {
    isVerifyingTaxInfo,
    isTaxInfoVerificationInconclusive,
    isTaxInfoVerificationEnabledForCountry,
    taxInfoVerificationResult,
    startTaxInfoVerification,
    resetTaxInfoVerificationResult,
  } = useTaxInfoVerification(merchantId);

  // The original intention was to resolve if the tax info has changed on the handleSubmit directly,
  // but that doesn't work as the dirty values are cleared up immediately after you hit submit.
  // This is the related issue from react-final-form: https://github.com/final-form/react-final-form/issues/861.
  const [hasTaxInfoChanged, setHasTaxInfoChanged] = useState(true);

  const handleSubmit = async (data: BasicInfoFormValues) => {
    resetTaxInfoVerificationResult();

    const representatives: (BusinessRepresentative | undefined)[] = data.businessRepresentatives || [];
    const ubos: (UboDetail | undefined)[] = submissionData.beneficiaries?.uboDetails || [];

    const isSoleTrader = data.legalForm === 'SOLE_TRADER';
    const singleRepresentative = representatives.length === 1;
    const representativeMatchesUbo = ubos.length === 1 && representatives[0]?.entityUUID === ubos[0]?.entityUUID;
    const singleRepresentativeAndNoUbos = ubos.length === 0;

    const isRepresentativeSoleBusinessOwner =
      isSoleTrader && singleRepresentative && (representativeMatchesUbo || singleRepresentativeAndNoUbos);

    const beneficiariesSubmissionData: Beneficiaries | null = {
      uboDetails: submissionData.beneficiaries?.uboDetails || null,
      pepDetails: submissionData.beneficiaries?.pepDetails || null,
      isRepresentativeSoleBusinessOwner,
    };

    const submitData: KybSubmissionData = {
      ...submissionData,
      basicCompanyInfo: transformBasicInfoFormValuesToApi(data),
      beneficiaries: beneficiariesSubmissionData,
    };

    await updateSubmissionData(processId, submitData);

    const hasTaxInfo = !!data?.vatId || !!data?.tin;

    const shouldVerifyTaxInfo = hasTaxInfo && hasTaxInfoChanged && isTaxInfoVerificationEnabledForCountry(countryCode);

    if (shouldVerifyTaxInfo) {
      const resultConfidenceLevel = await startTaxInfoVerification();

      if (resultConfidenceLevel === 'INCONCLUSIVE') {
        return;
      }
    }

    await onSubmitSuccess(data.nextSection ? data.nextSection : 'basic-info', !!data.nextSection);
  };

  return (
    <>
      {isVerifyingTaxInfo && (
        <LoadingOverlay>
          <FormattedMessage id="compliance.kyb.sections.basic-info.business-information.tax-verification.loader-description" />
        </LoadingOverlay>
      )}

      <SectionForm
        subtitle={t({ id: 'compliance.kyb.sections.basic-info.subtitle' })}
        initialFormValues={initialFormValues}
        schema={basicInfoFormSchema}
        feedback={feedback}
        onSubmit={handleSubmit}
      >
        {({ submitting }) => (
          <>
            <BasicInfoFormFields
              submitting={submitting}
              taxInfoVerificationResult={taxInfoVerificationResult}
              basicInfoFieldsConfig={basicInfoFieldsConfig}
              businessRepresentativeFieldsConfig={businessRepresentativeFieldsConfig}
              submissionData={submissionData}
              onTaxInfoValuesDirtyStatusChange={setHasTaxInfoChanged}
            />

            {isTaxInfoVerificationInconclusive && (
              <Alert variant="warning">
                <FormattedMessage id="compliance.kyb.sections.basic-info.business-information.tax-verification.inconclusive-verification-warning" />
              </Alert>
            )}
          </>
        )}
      </SectionForm>
    </>
  );
};

export default BasicInfo;
